"use client"

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/legacy/image'
import { isBroken, isServicePhoto } from '@utils/image/servicePhoto'

import styles from './WithFallback.module.css'

const ImageWithFallback = ({
    alt,
    className,
    customFallbackResolver,
    fallback = '/fallback/list.png',
    hideOnError = false,
    objectFit,
    priority,
    spinner = true,
    src,
    useNextImage = true,
    ...rest
}) => {
    const [imgSrc, setImgSrc] = useState(src)
    const [failed, setFailed] = useState(false)
    const imgRef = useRef(null)
    const wrapRef = useRef(null)
    // stara usługa zdjęć: bez /_next/image (pusta odpowiedź 200 przerywa optymalizator błędem sharp)
    const servicePhoto = isServicePhoto(src)

    const fallbackSrc = useMemo(() =>
        customFallbackResolver && customFallbackResolver() || fallback || '/fallback/list.png', [fallback, customFallbackResolver])

    // Błąd sprzed hydratacji nie wywołuje onError Reacta: sprawdzenie stanu obrazu po montowaniu.
    useEffect(() => {
        const img = imgRef.current || wrapRef.current?.querySelector('img')
        if (!isBroken(img)) return
        if (hideOnError) setFailed(true)
        else if (img.getAttribute('src') !== fallbackSrc) setImgSrc(fallbackSrc)
    }, [hideOnError, fallbackSrc])

    const isPriority = Boolean(priority)

    const onError = (e) => {
        e.target.id = fallbackSrc
        e.target.srcset = fallbackSrc
        setImgSrc(fallbackSrc)
    }

    // Ścieżka Cloudflare: surowy <img>. Dokładamy fetchpriority='high' dla obrazu LCP.
    // priority jest named-paramem, więc NIE trafia jako nieprawidłowy atrybut na <img>.
    // NIE zmieniać na camelCase fetchPriority: na tej wersji Reacta camelCase
    // renderuje zły atrybut (fetchPriority=), który przeglądarka ignoruje —
    // obraz LCP traci priorytet ładowania. Konsola deweloperska ostrzega
    // wtedy dodatkowo "React does not recognize the fetchPriority prop"
    // (ostrzeżenie dev-only, nie występuje w buildzie produkcyjnym) —
    // to nie jest sygnał, że coś tu jest do poprawy.
    // Karty nowej strony głównej: bez wrappera 3:2 (geometrię daje kontener karty),
    // przy błędzie obraz znika zamiast podmiany na /fallback/list.png.
    if (hideOnError) {
        return (
            <img
                {...rest}
                alt={alt}
                className={className}
                hidden={failed}
                onError={() => setFailed(true)}
                ref={imgRef}
                src={src}
                loading={isPriority ? 'eager' : (rest.loading || 'lazy')}
                fetchpriority={isPriority ? 'high' : undefined}
            />
        )
    }

    if (!useNextImage) {
        return (
            <div className={styles.wrap} data-cf="true">
                <img
                    {...rest}
                    alt={alt}
                    className={className}
                    decoding={rest.decoding || (servicePhoto && !isPriority ? 'async' : undefined)}
                    onError={onError}
                    ref={imgRef}
                    src={imgSrc}
                    loading={isPriority ? 'eager' : (rest.loading || 'lazy')}
                    fetchpriority={isPriority ? 'high' : undefined}
                />
            </div>
        )
    }

    // Ścieżka lokalna: next/legacy/image (layout intrinsic — obraz skaluje się do kontenera).
    // Migracja na next/image wymaga per-call-site tłumaczenia layout/objectFit + poprawek CSS.
    const image = (
        <Image
            {...rest}
            alt={alt}
            className={className}
            objectFit={objectFit}
            onError={onError}
            priority={isPriority}
            src={imgSrc}
            unoptimized={rest.unoptimized || servicePhoto}
        />
    )
    // display: contents — bez wpływu na układ; dostęp do <img> dla sprawdzenia po montowaniu
    return servicePhoto ? <span ref={wrapRef} style={{ display: 'contents' }}>{image}</span> : image
}

export default ImageWithFallback
